// Path: src/contexts/AccountContext.js
import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import api from '../services/api';
import { useAuth } from './AuthContext';

const AccountContext = createContext();

export const useAccount = () => useContext(AccountContext);

/** Coalesce concurrent GETs (React Strict Mode double mount, many consumers). */
let activeAgentsInflight = null;
let accountBaseInflight = null;

export const AccountProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [accountDetails, setAccountDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [activeAgents, setActiveAgents] = useState({ count: 0, agents: [] });

  const fetchActiveAgents = useCallback(async () => {
    if (activeAgentsInflight) return activeAgentsInflight;
    activeAgentsInflight = (async () => {
      try {
        const response = await api.get('/account/active-agents/');
        const data = await response.data;
        setActiveAgents({
          count: data.active_agents_count,
          agents: data.active_agents,
        });
        return data;
      } catch (err) {
        console.error('Error fetching active agents data:', err);
        return { count: 0, agents: [] };
      } finally {
        activeAgentsInflight = null;
      }
    })();
    return activeAgentsInflight;
  }, []);

  const fetchAccountDetails = useCallback(async () => {
    if (accountBaseInflight) return accountBaseInflight;
    setLoading(true);
    setError(null);
    accountBaseInflight = (async () => {
      try {
        const response = await api.get('/account/base/');
        const data = await response.data;
        setAccountDetails(data);
        setHasInitiallyLoaded(true);
        return data;
      } catch (err) {
        setError('Failed to load account information');
        throw err;
      } finally {
        setLoading(false);
        accountBaseInflight = null;
      }
    })();
    return accountBaseInflight;
  }, []);

  const fetchSubscriptionData = useCallback(async () => {
    try {
      const response = await api.get('/subscription/');
      const data = await response.data;
      setCurrentPlan(data?.plan);
      setSubscriptionData(data);
      return data;
    } catch (e) {
      console.error('Error fetching subscription data:', e);
      setSubscriptionData(null);
      return null;
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated && !hasInitiallyLoaded) {
      fetchAccountDetails();
      fetchSubscriptionData();
    } else if (!isAuthenticated) {
      setAccountDetails(null);
      setHasInitiallyLoaded(false);
      setSubscriptionData(null);
      setCurrentPlan(null);
      setActiveAgents({ count: 0, agents: [] });
    }
  }, [isAuthenticated, hasInitiallyLoaded, fetchAccountDetails, fetchSubscriptionData]);

  useEffect(() => {
    if (!isAuthenticated) return undefined;
    fetchActiveAgents();
    const interval = setInterval(() => {
      fetchActiveAgents();
    }, 60000);
    return () => clearInterval(interval);
  }, [isAuthenticated, fetchActiveAgents]);

  const refreshAccountDetails = useCallback(async () => {
    if (!isAuthenticated) return null;
    return fetchAccountDetails();
  }, [isAuthenticated, fetchAccountDetails]);

  const isNewTeamAllowed = useCallback(async () => {
    try {
      const response = await api.get('/account/new_team_allowed/');
      return await response.data;
    } catch (e) {
      console.error('Error checking new team allowed:', e);
      return false;
    }
  }, []);

  const updateLeadManager = useCallback(async (userId, isLeadManager) => {
    try {
      return await api.post(`/account/update-lead-manager/${userId}/`, { is_lead_manager: isLeadManager });
    } catch (e) {
      console.error('Error updating lead manager:', e);
      throw e;
    }
  }, []);

  const value = useMemo(
    () => ({
      accountDetails,
      loading,
      error,
      refreshAccountDetails,
      companyName: accountDetails?.company_name || '',
      accountId: accountDetails?.account_id,
      planName: accountDetails?.plan_name || 'Free',
      voiceCredits: accountDetails?.voice_credits || 0,
      daysLeft: accountDetails?.days_left || 0,
      validityStr: accountDetails?.validity_str || '',
      expired: accountDetails?.expired || false,
      industry: accountDetails?.industry || '',
      currentPlan,
      subscriptionData,
      setSubscriptionData,
      setCurrentPlan,
      fetchSubscriptionData,
      isNewTeamAllowed,
      activeAgents,
      fetchActiveAgents,
      updateLeadManager,
    }),
    [
      accountDetails,
      loading,
      error,
      refreshAccountDetails,
      currentPlan,
      subscriptionData,
      fetchSubscriptionData,
      isNewTeamAllowed,
      activeAgents,
      fetchActiveAgents,
      updateLeadManager,
    ]
  );

  return <AccountContext.Provider value={value}>{children}</AccountContext.Provider>;
};

export default AccountContext;
